// src/components/dashboards/charts/BiomarkerRadar.tsx
// Radar chart: biomarker averages (TMB, PD-L1 TPS/CPS, Ki-67, MSI-H, TMB-H).

import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "./useChartTheme";
import { BiomarkersData } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: BiomarkersData | null;
  loading: boolean;
}

const BiomarkerRadar: React.FC<Props> = ({ data, loading }) => {
  const { baseTooltip, seriesColors, textPrimary, textSecondary, chartHeight } = useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return <Skeleton variant="rounded" height={chartHeight + 20} sx={{ borderRadius: 2 }} />;
  }

  const markers = data.biomarkers.filter((b) => b.count > 0);
  if (markers.length === 0) {
    return (
      <Box sx={{ height: chartHeight + 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.analytics.no_biomarker_data", "No biomarker data available")}
        </Typography>
      </Box>
    );
  }

  // Normalize values to 0-100 for radar display
  const maxVals = markers.map((m) => Math.max(m.value * 1.3, 1));

  const option = {
    title: {
      text: t("dashboard.analytics.biomarker_radar", "Biomarker Profile"),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      left: 0,
    },
    tooltip: { ...baseTooltip },
    radar: {
      indicator: markers.map((m, i) => ({
        name: m.name,
        max: maxVals[i],
      })),
      shape: "polygon",
      splitNumber: 4,
      axisName: { color: textSecondary, fontSize: 10 },
      splitLine: { lineStyle: { color: "rgba(128,128,128,0.2)" } },
      splitArea: { show: true, areaStyle: { color: ["rgba(128,128,128,0.05)", "rgba(128,128,128,0.1)"] } },
      axisLine: { lineStyle: { color: "rgba(128,128,128,0.3)" } },
    },
    series: [
      {
        type: "radar",
        data: [
          {
            value: markers.map((m) => m.value),
            name: "Cohort Average",
            areaStyle: { color: `${seriesColors[0]}33` },
            lineStyle: { color: seriesColors[0], width: 2 },
            itemStyle: { color: seriesColors[0] },
          },
        ],
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight + 20 }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default BiomarkerRadar;
